import React, { useState, useEffect } from 'react';
import APIService, { urlAvecJeton } from '../services/api';
import htmlSur from '../services/htmlSur';
import CvEditeurLauncher from './CvEditeurLauncher';
import { avecCle } from '../services/cleLecture';

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;

const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const full = (c) => [c?.prenom, c?.nom].filter(Boolean).join(' ').trim();

// Pièce administrative évidente (CNI, permis, passeport, AVS…) : jamais un CV,
// même estampillée type_piece « cv » par un import. Ancré au début du nom pour
// épargner un vrai CV mentionnant « permis B » dans son titre.
const estPieceAdmin = (d) => /^\s*(cni|carte\s+(nationale\s+)?d.{0,2}identit|pi[eè]ce\s+d.{0,2}identit|permis|passeport|avs|rib|iban|dipl[oô]me|attestation|certificat|extrait|casier|titre\s+de\s+s[ée]jour)\b/i
  .test(String(d?.label || d?.name || d?.original_name || '').trim());

// CV principal depuis data.documents (le back le met en premier ; is_principal le
// confirme). Repli sur type_piece/label « cv », puis premier document non
// administratif — l'aperçu « CV » ne doit jamais montrer une CNI.
const pickCvDoc = (c) => {
  const docs = Array.isArray(c?.documents) ? c.documents : [];
  if (!docs.length) return null;
  const nonAdmin = docs.filter(d => !estPieceAdmin(d));
  return docs.find(d => d.is_principal)
    || nonAdmin.find(d => /^cv/i.test(d.type_piece || '') || /\bcv\b/i.test(d.label || ''))
    || nonAdmin[0] || null;
};

// ── Aperçu CV plein écran ──
// 1) document canonique (docId) → viewer serveur GET …/documents/:docId/preview
//    (docx→pdf converti côté back, clé de lecture en query) — le viewer habituel.
// 2) cv_preview_url servi par le back : un PDF, même pour un .docx — rendu en
//    <iframe>, sans conversion dans le navigateur.
// 3) repli legacy : cv_url seul → cv-proxy + mammoth pour le DOCX.
function CompactCVViewer({ candidat, doc, onClose, showNotification }) {
  const [editerCv, setEditerCv] = useState(false);
  // Incrémenté à la fermeture de l'éditeur : l'édition régénère le document
  // côté serveur — l'aperçu doit montrer le fichier d'après, pas le cache.
  const [version, setVersion] = useState(0);
  const docId = doc?.id ?? null;
  const previewUrl = docId != null ? avecCle(`${BASE}/candidates/${candidat.id}/documents/${docId}/preview?v=${version}`) : null;
  // Le serveur sait rendre un PDF, y compris depuis un .docx. Quand il le
  // propose, on ne convertit plus rien dans le navigateur : moins de code,
  // et surtout plus de HTML tiers à injecter.
  // Rendue en <iframe> : le jeton passe en query, comme le viewer canonique.
  const cvPreviewUrl = !previewUrl ? urlAvecJeton(candidat?.cv_preview_url || null) : null;
  const label = doc?.original_name || doc?.label || candidat.cv_original_name || 'CV du candidat';
  const isImg = /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(label);

  const [blobUrl, setBlobUrl] = useState(null);
  const [docxHtml, setDocxHtml] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    // Cas 1 et 2 : le serveur rend le fichier → l'<iframe> gère le chargement.
    if (previewUrl || cvPreviewUrl) { setLoading(false); return undefined; }
    // Cas 3 : repli cv-proxy + mammoth (ni document, ni aperçu serveur).
    let cancelled = false, createdUrl = null;
    if (!candidat?.cv_url) { setLoading(false); setError(true); return undefined; }
    fetch(`${BASE}/candidats/cv-proxy/${candidat.id}`, { headers: authH() })
      .then(r => r.ok ? r.arrayBuffer() : Promise.reject(r.status))
      .then(async (buf) => {
        if (cancelled) return;
        const sig = new Uint8Array(buf.slice(0, 2));
        const nm = (candidat.cv_original_name || candidat.cv_url || '').toLowerCase();
        const looksDocx = /\.docx?($|\?)/.test(nm) || /word|officedocument/.test(nm) || (sig[0] === 0x50 && sig[1] === 0x4B);
        if (looksDocx) {
          const mod = await import('mammoth');
          const mammoth = mod.default || mod;
          const { value } = await mammoth.convertToHtml({ arrayBuffer: buf });
          if (cancelled) return;
          setDocxHtml((value || '').trim() || '<p style="color:#888"><em>Document sans texte affichable — téléchargez-le pour l\'ouvrir dans Word.</em></p>');
        } else {
          createdUrl = URL.createObjectURL(new Blob([buf], { type: 'application/pdf' }));
          setBlobUrl(createdUrl);
        }
        if (!cancelled) setLoading(false);
      })
      .catch(() => { if (!cancelled) { setError(true); setLoading(false); } });
    return () => { cancelled = true; if (createdUrl) URL.revokeObjectURL(createdUrl); };
  }, [candidat, previewUrl]);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '900px', height: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--accent, #1a1a1a)', color: '#fff' }}>
          <span style={{ fontSize: '13.5px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {full(candidat) || 'CV'} — {label}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 'none' }}>
            {/* Éditer : réservé aux CV générés par le générateur — sur un CV
                déposé à la main, l'éditeur n'a aucune préparation derrière lui. */}
            {(doc?.source === 'generation' || doc?.source === 'cv_prep' || doc?.kind === 'generated') && (
            <button onClick={() => setEditerCv(true)} title="Modifier le contenu du CV (sections, expériences, compétences)"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'rgba(255,255,255,0.14)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: 600, padding: '5px 9px', borderRadius: '7px', fontFamily: 'inherit' }}>
              <BI icon="pencil-square" /> Éditer
            </button>
            )}
            <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '16px', padding: '4px' }} title="Fermer"><BI icon="x-lg" /></button>
          </span>
        </div>
        <div style={{ flex: 1, minHeight: 0, background: '#fff', overflow: 'auto', position: 'relative' }}>
          {loading && <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#888', fontSize: '13px' }}>Chargement du CV…</div>}
          {error && !loading && <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#888', gap: '8px' }}><BI icon="exclamation-circle" style={{ fontSize: '26px' }} /><span>Aucun CV disponible</span></div>}
          {/* Cas 1 : viewer canonique */}
          {previewUrl && !error && (isImg
            ? <img src={previewUrl} alt={label} onLoad={() => setLoading(false)} onError={() => { setLoading(false); setError(true); }} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block', margin: '0 auto' }} />
            : <iframe /* pas de sandbox : cf. DocumentViewer */src={previewUrl} title="CV" onLoad={() => setLoading(false)} style={{ width: '100%', height: '100%', border: 'none' }} />)}
          {/* Cas 2 : repli cv-proxy */}
          {!previewUrl && !cvPreviewUrl && docxHtml && !loading && !error && (
            <div style={{ maxWidth: '820px', margin: '0 auto', padding: '40px 48px', fontFamily: 'Calibri, Arial, sans-serif', fontSize: '11pt', lineHeight: 1.5, color: '#111' }} dangerouslySetInnerHTML={{ __html: htmlSur(docxHtml) }} />
          )}
          {!previewUrl && cvPreviewUrl && !loading && !error && (
            <iframe /* pas de sandbox : cf. DocumentViewer */src={cvPreviewUrl} title="CV" onLoad={() => setLoading(false)} style={{ width: '100%', height: '100%', border: 'none' }} />
          )}
          {!previewUrl && !cvPreviewUrl && blobUrl && !loading && !error && <iframe /* pas de sandbox : cf. DocumentViewer */src={blobUrl} title="CV" style={{ width: '100%', height: '100%', border: 'none' }} />}
        </div>
      </div>
      {editerCv && (
        <div onClick={e => e.stopPropagation()}>
          <CvEditeurLauncher candidateId={candidat?.id} docId={docId} candidatNom={full(candidat)}
            showNotification={showNotification} zIndex={1400} onClose={() => { setEditerCv(false); setVersion(v => v + 1); }} />
        </div>
      )}
    </div>
  );
}

// ── Un panneau (drawer) empilé : charge la fiche candidat en aperçu compact. ──
function PreviewDrawer({ stub, index, onClose, onOpenFull, showNotification }) {
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [cvOpen, setCvOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setNotFound(false);
    fetch(`${BASE}/candidats/vue/${stub.id}`, { headers: authH() })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(j => { if (!cancelled) { setC(j.data || j.candidat || j); setLoading(false); } })
      .catch(() => { if (!cancelled) { setNotFound(true); setLoading(false); } });
    return () => { cancelled = true; };
  }, [stub.id]);

  // Empilement : panneaux côte à côte, à GAUCHE du drawer de chat (≈440px) pour ne
  // pas le recouvrir → « plusieurs fenêtres » visibles en même temps.
  const width = 360;
  const right = 470 + index * (width + 14);
  const title = full(c) || stub.title || 'Candidat';
  const poste = c?.titre_poste || c?.poste || c?.titre || stub.subtitle || '';
  const ville = c?.ville || c?.localisation || '';
  const email = c?.email || '';
  const tel = c?.telephone || c?.tel || c?.mobile || '';
  const statut = c?.statut || '';
  const score = stub.score;
  // CV : document canonique (data.documents) en priorité, sinon cv_url legacy.
  const cvDoc = pickCvDoc(c);
  const hasCv = !!cvDoc || !!c?.cv_url;

  const line = (icon, val) => val ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: 'var(--text-2, #444)' }}>
      <BI icon={icon} style={{ color: 'var(--text-3, #999)', flex: 'none', width: '15px' }} />
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</span>
    </div>
  ) : null;

  return (
    <>
      <div style={{
        position: 'fixed', top: '78px', right: `${right}px`, bottom: '20px', width: `${width}px`,
        background: 'var(--panel, #fff)', border: '1px solid var(--border, #e5e5e5)', borderRadius: '14px',
        boxShadow: '0 12px 40px rgba(0,0,0,0.18)', zIndex: 1100 + index, display: 'flex', flexDirection: 'column',
        overflow: 'hidden', fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', borderBottom: '1px solid var(--hair, #eee)' }}>
          <span style={{ width: '30px', height: '30px', borderRadius: '8px', background: 'var(--chip-bg, #f2f2f2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}><BI icon="person" style={{ color: 'var(--text-2)' }} /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-strong, #111)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
            {poste && <div style={{ fontSize: '12px', color: 'var(--text-2, #666)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{poste}</div>}
          </div>
          {score != null && <span style={{ fontSize: '11px', fontWeight: 700, color: '#16a34a', background: 'rgba(22,163,74,0.12)', padding: '2px 7px', borderRadius: '6px', flex: 'none' }}>{Math.round(score)}%</span>}
          <button onClick={onClose} title="Fermer" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-3, #999)', padding: '4px', flex: 'none' }}><BI icon="x-lg" /></button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '14px', display: 'flex', flexDirection: 'column', gap: '9px' }}>
          {loading && <div style={{ color: 'var(--text-faint, #aaa)', fontSize: '12.5px', textAlign: 'center', padding: '20px 0' }}>Chargement de la fiche…</div>}
          {notFound && !loading && <div style={{ color: 'var(--text-faint, #aaa)', fontSize: '12.5px', textAlign: 'center', padding: '20px 0' }}><BI icon="exclamation-circle" /> Candidat introuvable</div>}
          {!loading && !notFound && (
            <>
              {statut && <div><span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-2)', background: 'var(--chip-bg, #f2f2f2)', padding: '3px 9px', borderRadius: '6px' }}>{statut}</span></div>}
              {line('geo-alt', ville)}
              {line('envelope', email)}
              {line('telephone', tel)}
              {!ville && !email && !tel && !statut && <div style={{ fontSize: '12px', color: 'var(--text-faint, #aaa)' }}>Aucune coordonnée renseignée.</div>}
              <div style={{ marginTop: '4px' }}>
                <button onClick={() => setCvOpen(true)} disabled={!hasCv}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', width: '100%', justifyContent: 'center', padding: '8px', borderRadius: '9px', border: '1px solid var(--input-border, #ddd)', background: 'var(--ghost-bg, #fafafa)', color: 'var(--text, #222)', fontSize: '12.5px', fontWeight: 600, cursor: hasCv ? 'pointer' : 'not-allowed', opacity: hasCv ? 1 : 0.5, fontFamily: 'inherit' }}>
                  <BI icon="file-earmark-text" /> {hasCv ? 'Voir le CV' : 'Pas de CV'}
                </button>
              </div>
            </>
          )}
        </div>

        <div style={{ padding: '12px 14px', borderTop: '1px solid var(--hair, #eee)' }}>
          <button onClick={() => onOpenFull(stub.id)}
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '7px', width: '100%', padding: '9px', borderRadius: '9px', border: 'none', background: 'var(--accent, #1a1a1a)', color: '#fff', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            Ouvrir la fiche complète <BI icon="box-arrow-up-right" />
          </button>
        </div>
      </div>
      {cvOpen && c && <CompactCVViewer candidat={c} doc={cvDoc} showNotification={showNotification} onClose={() => setCvOpen(false)} />}
    </>
  );
}

/**
 * Pile de panneaux d'aperçu candidat, ouverts par l'assistant IA.
 * Non-modal (pas de fond assombri) → l'app reste utilisable. Plusieurs fiches
 * peuvent être ouvertes côte à côte ; chacune se ferme indépendamment.
 */
export default function CandidatPreviewDrawers({ items = [], onClose, onCloseAll, onOpenFull, showNotification }) {
  if (!items.length) return null;
  return (
    <>
      {items.length > 1 && (
        <button onClick={onCloseAll} title="Tout fermer"
          style={{ position: 'fixed', top: '44px', right: '470px', zIndex: 1300, display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '8px', border: '1px solid var(--border, #ddd)', background: 'var(--panel, #fff)', color: 'var(--text-2, #555)', fontSize: '12px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,0,0,0.12)', fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
          <BI icon="x-circle" /> Tout fermer ({items.length})
        </button>
      )}
      {items.map((stub, i) => (
        <PreviewDrawer key={`${stub.id}-${i}`} stub={stub} index={i} onClose={() => onClose(stub.id)} onOpenFull={onOpenFull} showNotification={showNotification} />
      ))}
    </>
  );
}
